/**
 * FeatureVideoModal — in-place "How it Works" player for a batch feature.
 * Portalled to <body> so it floats above the Explore modal (which is a
 * transformed, scrollable container). Autoplays immediately; closes on ✕,
 * backdrop click or Escape.
 */
import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { Play, X } from "lucide-react";

const buildSrc = (video) => {
  if (!video?.embed_url) return null;
  if (video.source === "youtube") {
    return `${video.embed_url}?autoplay=1&rel=0&modestbranding=1&playsinline=1&iv_load_policy=3`;
  }
  const sep = video.embed_url.includes("?") ? "&" : "?";
  return `${video.embed_url}${sep}autoplay=true&preload=true&responsive=true`;
};

export default function FeatureVideoModal({ item, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  if (!item?.video) return null;
  const src = buildSrc(item.video);
  const label = item.video.label || "How it Works";

  return createPortal(
    <div className="fixed inset-0 z-[140] flex items-center justify-center p-3 sm:p-6" data-testid="feature-video-modal">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="absolute inset-0 bg-[#030a18]/85 backdrop-blur-md"
        onClick={onClose}
        data-testid="feature-video-overlay"
      />
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 16, scale: 0.97 }}
        transition={{ type: "spring", damping: 26, stiffness: 300 }}
        className="relative w-full max-w-4xl rounded-2xl sm:rounded-3xl overflow-hidden border border-white/10 bg-[#061226]"
        style={{ boxShadow: "0 30px 90px -20px rgba(0,0,0,0.8), 0 0 0 1px rgba(92,238,255,0.08), 0 0 80px -30px rgba(92,238,255,0.35)" }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`${label}: ${item.text}`}
      >
        <div className="flex items-center gap-3 px-4 sm:px-5 py-3 border-b border-white/[0.07]">
          <span className="inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full bg-[#5ceeff]/[0.10] border border-[#5ceeff]/30 text-[#5ceeff] text-[9.5px] lms-mono uppercase tracking-[0.16em] shrink-0">
            <Play size={9} fill="currentColor" strokeWidth={0} /> {label}
          </span>
          <div className="flex-1 min-w-0 text-white text-sm sm:text-[15px] font-medium truncate" data-testid="feature-video-title">{item.text}</div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 -mr-1 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition"
            aria-label="Close video"
            data-testid="feature-video-close"
          >
            <X size={17} />
          </button>
        </div>
        <div className="relative bg-black" style={{ aspectRatio: "16 / 9" }}>
          {src && (
            <iframe
              src={src}
              title={`${label} — ${item.text}`}
              allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
              style={{ border: 0 }}
              data-testid="feature-video-iframe"
            />
          )}
        </div>
      </motion.div>
    </div>,
    document.body
  );
}
